class TestimonialsSlider extends SliderComponent {
  constructor() {
    super();
    this.autoplaySpeed = 5000;
    this.autoplayInterval = null;
    this.resumeTimeout = null;
    this.startDelayTimeout = null;
    this.autoScrollTimeout = null;
    this.resumeDelay = 3000;
    this.isInView = false;
    this.isAutoScrolling = false;
    this.observer = null;

    this.onUserInteraction = this.onUserInteraction.bind(this);
    this.scheduleResume = this.scheduleResume.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
  }

  connectedCallback() {
    const parsedSpeed = parseInt(this.dataset.autoplaySpeed, 10);
    if (!isNaN(parsedSpeed) && parsedSpeed > 0) this.autoplaySpeed = parsedSpeed;

    this.slider = this.querySelector('[id^="TestimonialsSlider-"]');
    this.sliderItems = this.querySelectorAll('[id^="TestimonialSlide-"]');

    if (!this.slider || this.sliderItems.length === 0) return;

    this.initPages();

    if (this.sliderItems.length > 1) {
      this.initIntersectionObserver();
      this.addEventListeners();
    }
  }

  get slideStep() {
    if (this.sliderItems.length > 1) {
      const step = this.sliderItems[1].offsetLeft - this.sliderItems[0].offsetLeft;
      if (step > 0) return step;
    }
    return this.sliderItems[0].offsetWidth;
  }

  initIntersectionObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.isInView = entry.isIntersecting;
          if (entry.isIntersecting) {
            this.initAutoplay();
          } else {
            this.pauseAutoplay();
          }
        });
      },
      {
        rootMargin: '50px 0px 50px 0px',
        threshold: 0.1,
      }
    );

    this.observer.observe(this);
  }

  initAutoplay() {
    if (!this.isInView) return;

    clearTimeout(this.startDelayTimeout);
    this.startDelayTimeout = setTimeout(() => {
      if (this.isInView) this.startAutoplay();
    }, 1000);
  }

  addEventListeners() {
    this.slider.addEventListener('touchstart', this.onUserInteraction, { passive: true });
    this.slider.addEventListener('touchmove', this.onUserInteraction, { passive: true });
    this.slider.addEventListener('scroll', this.onUserInteraction, { passive: true });

    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.addEventListener('focusin', this.onUserInteraction);
    this.addEventListener('focusout', this.scheduleResume);
  }

  onUserInteraction() {
    if (this.isAutoScrolling) return;

    this.pauseAutoplay();
    this.scheduleResume();
  }

  scheduleResume() {
    clearTimeout(this.resumeTimeout);

    this.resumeTimeout = setTimeout(() => {
      if (this.isInView) this.startAutoplay();
    }, this.resumeDelay);
  }

  onVisibilityChange() {
    if (document.hidden) {
      this.pauseAutoplay();
    } else if (this.isInView) {
      this.scheduleResume();
    }
  }

  startAutoplay() {
    if (!this.isInView) return;

    this.pauseAutoplay();
    this.autoplayInterval = setInterval(() => {
      if (this.isInView) this.autoAdvance();
    }, this.autoplaySpeed);
  }

  pauseAutoplay() {
    clearInterval(this.autoplayInterval);
    this.autoplayInterval = null;
  }

  autoAdvance() {
    if (!this.slider || this.sliderItems.length <= 1 || !this.isInView) return;

    this.isAutoScrolling = true;

    const step = this.slideStep;
    const nextIndex = Math.round(this.slider.scrollLeft / step) + 1;
    const target = nextIndex >= this.sliderItems.length ? 0 : nextIndex * step;

    this.slider.scrollTo({ left: target, behavior: 'smooth' });

    clearTimeout(this.autoScrollTimeout);
    this.autoScrollTimeout = setTimeout(() => {
      this.isAutoScrolling = false;
    }, 500);
  }

  setSlidePosition(position) {
    if (!this.slider) return;

    this.slider.scrollTo({ left: position, behavior: 'smooth' });
  }

  disconnectedCallback() {
    this.pauseAutoplay();
    clearTimeout(this.resumeTimeout);
    clearTimeout(this.startDelayTimeout);
    clearTimeout(this.autoScrollTimeout);

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }
}

customElements.define('testimonials-slider', TestimonialsSlider);
